import * as React from 'react';
import { VFFieldProps } from '../types';
import classNames from 'classnames';
import * as Classes from '../../common/classes';
import { StringInput } from '../../string/StringInput';
import { useMemo } from 'react';
import { FormGroup } from '../../form-group/FormGroup';
import { useField } from 'react-final-form';

export type ValueDetailProps = Omit<
  VFFieldProps<React.ComponentProps<typeof StringInput>> & {
    /**
     * Pokud není uvedeno name, definuje se tímto parametrem hodnota - hodnota pro zobrazení, prvek se zobrazuje dle toho jakého je typu - number - jako číslo, string jak string, ...
     */
    value?: any;
    /**
     * Pokud true, je zobrazováno jako CZK měna, pokud řetězec (např. CZK, je použit pro intl formát).
     */
    currency?: boolean | string;
    /**
     * Pokud je true, formátuje se jako desetinné číslo, jinak celé (lze kombinovat s currency).
     */
    decimal?: boolean;
    /**
     * Pokud je true, tak detail nebude u čísel oddělovat tisíce.
     */
    noThousandsSeparator?: boolean;
  },
  'name' | 'label'
> & {
  label?: React.ReactNode;
  /**
   * Název pro formulářový prvek, pokud je uvedeno, nepoužívá se value, ale hodnota se pomocí useField bere z formuláře.
   */
  name?: string;
};

export const Detail = React.forwardRef<HTMLInputElement, ValueDetailProps>(
  (
    {
      decimal,
      currency,
      className,
      inputClassName,
      contentClassName,
      label,
      value,
      disabled,
      inlineValidation,
      noThousandsSeparator,
      name,
      ...other
    },
    ref
  ) => {
    let realValue = value;
    if (name) {
      const f = useField(name);
      realValue = f.input.value;
    }

    const numberFormatter = useMemo(() => {
      let useCurrency;
      if (typeof currency === 'boolean') {
        useCurrency = 'CZK';
      } else {
        useCurrency = currency;
      }
      if (!!decimal) {
        return new Intl.NumberFormat('cs-CZ', {
          style: currency !== undefined ? 'currency' : undefined,
          currency: useCurrency,
          minimumFractionDigits: currency !== undefined ? 2 : 0,
          useGrouping: !noThousandsSeparator,
          maximumFractionDigits: 2,
        });
      } else {
        return new Intl.NumberFormat('cs-CZ', {
          style: currency !== undefined ? 'currency' : undefined,
          currency: useCurrency,
          useGrouping: !noThousandsSeparator,
          maximumFractionDigits: 0,
        });
      }
    }, [currency, decimal]);

    const cls = classNames(
      Classes.FORM_GROUP,
      Classes.FORM_GROUP_FORM,
      Classes.RF_FORM_GROUP,
      className,
      {
        [Classes.RF_NO_ERROR_SPACE]: inlineValidation,
      }
    );

    let formattedValue;
    if (typeof realValue === 'number') {
      formattedValue = numberFormatter.format(realValue);
    } else {
      formattedValue = realValue;
    }

    return (
      <FormGroup
        className={cls}
        label={label}
        contentClassName={contentClassName}
      >
        <StringInput
          className={inputClassName}
          // @ts-ignore
          ref={ref}
          {...other}
          readOnly
          disabled={disabled}
          value={formattedValue}
        />
      </FormGroup>
    );
  }
);
